import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import T, { SUPPORTED_LANGS, COUNTRY_DEFAULTS } from "@/i18n/translations";

const I18nCtx = createContext(null);

// Re-export so existing imports `import { LANGUAGES } from '@/contexts/I18nContext'`
// keep working without churn elsewhere.
export const LANGUAGES = SUPPORTED_LANGS;

const LS_LANG = "clariva_lang";
const LS_DETECTED = "clariva_locale_detected"; // marker to only auto-detect once
const LS_CURRENCY = "clariva_currency";
const LS_COUNTRY = "clariva_country";

const RTL_LANGS = new Set(SUPPORTED_LANGS.filter((l) => l.rtl).map((l) => l.code));

function bcp47For(lang) {
  // Map our 2-letter codes to richer BCP-47 tags Intl.* recognises well.
  const map = { en: "en-US", ar: "ar-AE", fr: "fr-FR", es: "es-ES", de: "de-DE", ja: "ja-JP", zh: "zh-CN", ko: "ko-KR" };
  return map[lang] || lang;
}

function defaultCurrencyForLang(lang) {
  const found = SUPPORTED_LANGS.find((l) => l.code === lang);
  return found?.currency || "USD";
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LS_LANG) || "en"; } catch { return "en"; }
  });
  const [country, setCountry] = useState(() => {
    try { return localStorage.getItem(LS_COUNTRY) || null; } catch { return null; }
  });
  const [currency, setCurrency] = useState(() => {
    // If a country was geo-pinned, trust the stored currency. Otherwise derive
    // from the current language's default — keeps currency aligned with lang
    // even after manual language switches in earlier sessions.
    let storedCcy = null, storedCountry = null, storedLang = "en";
    try {
      storedCcy = localStorage.getItem(LS_CURRENCY);
      storedCountry = localStorage.getItem(LS_COUNTRY);
      storedLang = localStorage.getItem(LS_LANG) || "en";
    } catch {}
    if (storedCountry && storedCcy) return storedCcy;
    return defaultCurrencyForLang(storedLang);
  });
  const [autoDetected, setAutoDetected] = useState(null); // banner state on first visit only

  // ---------- First-visit auto-detect ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (localStorage.getItem(LS_DETECTED) === "1") return; // already done

        const { data } = await api.get("/locale/detect");
        if (cancelled || !data) return;
        const ok = SUPPORTED_LANGS.some((l) => l.code === data.lang);
        if (!ok) return;

        // Only nudge if the detected lang differs from current
        if (data.lang !== lang) {
          setLang(data.lang);
          localStorage.setItem(LS_LANG, data.lang);
          setAutoDetected({ from: lang, to: data.lang, source: data.source });
        }
        if (data.currency) {
          setCurrency(data.currency);
          localStorage.setItem(LS_CURRENCY, data.currency);
        }
        if (data.country) {
          setCountry(data.country);
          localStorage.setItem(LS_COUNTRY, data.country);
        }
      } catch (_e) {
        // Network/server failure — stick with whatever the user already has
      } finally {
        try { localStorage.setItem(LS_DETECTED, "1"); } catch {}
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- DOM side effects ----------
  useEffect(() => {
    try { localStorage.setItem(LS_LANG, lang); } catch {}
    document.documentElement.lang = lang;
    document.documentElement.setAttribute("dir", RTL_LANGS.has(lang) ? "rtl" : "ltr");
  }, [lang]);

  useEffect(() => {
    try { localStorage.setItem(LS_CURRENCY, currency); } catch {}
  }, [currency]);

  // ---------- Public API ----------
  const t = (key, vars) => {
    const dict = T[lang] || T.en;
    let value = dict[key] != null ? dict[key] : T.en[key];
    if (value == null) return key;
    if (vars && typeof value === "string") {
      for (const [k, v] of Object.entries(vars)) {
        value = value.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
      }
    }
    return value;
  };

  const changeLang = async (newLang) => {
    if (!SUPPORTED_LANGS.some((l) => l.code === newLang)) return;
    setLang(newLang);
    setAutoDetected(null);

    // Auto-update currency to the language's default — but only if the user
    // hasn't explicitly chosen a country (geo-detected ones stay as-is).
    // This fixes the case where switching to fr-FR keeps USD, or zh-CN keeps INR.
    let pinnedByCountry = false;
    try { pinnedByCountry = !!localStorage.getItem(LS_COUNTRY); } catch {}
    if (!pinnedByCountry) {
      const nextCcy = defaultCurrencyForLang(newLang);
      setCurrency(nextCcy);
    }

    // Sync to backend only when there's a logged-in token. For guests on the
    // Landing page we skip the patch — otherwise our axios 401 interceptor
    // would yank the visitor over to /login on every language change.
    let hasToken = false;
    try { hasToken = !!localStorage.getItem("clariva_token"); } catch {}
    if (hasToken) {
      try { await api.patch("/profile", { language: newLang }); } catch {}
    }
  };

  const changeCurrency = (ccy) => {
    if (!ccy || typeof ccy !== "string") return;
    setCurrency(ccy.toUpperCase());
  };

  // -------- Formatters (memoised on lang/currency change) --------
  const formatters = useMemo(() => {
    const tag = bcp47For(lang);
    const moneyFmt = new Intl.NumberFormat(tag, { style: "currency", currency, maximumFractionDigits: 0 });
    const moneyFmt2 = new Intl.NumberFormat(tag, { style: "currency", currency, maximumFractionDigits: 2 });
    const numberFmt = new Intl.NumberFormat(tag);
    const dateFmtFull = new Intl.DateTimeFormat(tag, { dateStyle: "long" });
    const dateFmtShort = new Intl.DateTimeFormat(tag, { dateStyle: "medium" });
    const relativeFmt = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
    return { tag, moneyFmt, moneyFmt2, numberFmt, dateFmtFull, dateFmtShort, relativeFmt };
  }, [lang, currency]);

  /** Format an amount in the user's preferred currency.
   *  Caller can override the currency per-call (e.g. when displaying a
   *  contract that's denominated in a fixed currency).
   */
  const formatMoney = (amount, opts = {}) => {
    const { currency: forced, decimals } = opts;
    if (amount == null || isNaN(Number(amount))) return "—";
    if (forced && forced !== currency) {
      try {
        return new Intl.NumberFormat(formatters.tag, {
          style: "currency",
          currency: forced,
          maximumFractionDigits: decimals ?? 0,
        }).format(Number(amount));
      } catch {
        return `${forced} ${Number(amount).toFixed(decimals ?? 0)}`;
      }
    }
    return (decimals === 2 ? formatters.moneyFmt2 : formatters.moneyFmt).format(Number(amount));
  };

  const formatNumber = (n) => {
    if (n == null || isNaN(Number(n))) return "—";
    return formatters.numberFmt.format(Number(n));
  };

  const formatDate = (input, style = "medium") => {
    if (!input) return "—";
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return "—";
    return (style === "long" ? formatters.dateFmtFull : formatters.dateFmtShort).format(d);
  };

  const dismissBanner = () => setAutoDetected(null);

  const value = {
    // Language
    lang, changeLang, t,
    // Currency
    currency, changeCurrency,
    // Geo
    country,
    // Direction
    dir: RTL_LANGS.has(lang) ? "rtl" : "ltr",
    rtl: RTL_LANGS.has(lang),
    // Banner
    autoDetected, dismissBanner,
    // Formatters
    formatMoney, formatNumber, formatDate,
    locale: bcp47For(lang),
    SUPPORTED_LANGS, COUNTRY_DEFAULTS,
  };

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
/** Alias — readers that only want locale/currency/formatters can import a
 *  shorter name without pulling in `t()`. */
export const useLocale = () => useContext(I18nCtx);
